import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Save, FileText } from 'lucide-react';
import '../styles/MasterForms.css';
import apiClient from '../services/api';

const JobType = () => {
  // Get session data
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;
  const yearId = session?.yearid;

  // State
  const [jobTypes, setJobTypes] = useState([]);
  const [jobModes, setJobModes] = useState([]);
  const [revenueTypes, setRevenueTypes] = useState([]);
  const [filteredJobTypes, setFilteredJobTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    JobTypeID: null,
    JobDescription: '',
    JobModeID: '',
    JobCode: '',
    JobPrefix: '',
    StatusSea: 1, // 1 = Sea, 0 = Air
    Remarks: ''
  });
  
  // Revenue grid data
  const [revenueItems, setRevenueItems] = useState([
    { id: Date.now(), RevenueTypeID: '', Amount: 0, Cost: 0, RevenueType: '', RevenueCode: '' }
  ]);
  
  const [errors, setErrors] = useState({});

  // Load data on mount
  useEffect(() => {
    if (!companyId || !yearId) {
      setLoading(false);
      return;
    }
    fetchJobModes();
    fetchRevenueTypes();
    fetchJobTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, yearId]);

  useEffect(() => {
    filterJobTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, jobTypes]);

  const fetchJobModes = async () => {
    try {
      const response = await apiClient.get('/JobMode.php');
      
      if (response.data.success) {
        setJobModes(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching job modes:', error);
    }
  };

  const fetchRevenueTypes = async () => {
    try {
      const response = await apiClient.get('/RevenueType.php');
      
      if (response.data.success) {
        setRevenueTypes(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching revenue types:', error);
    }
  };

  const fetchJobTypes = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/JobType.php');
      
      if (response.data.success) {
        setJobTypes(response.data.data || []);
      } else {
        alert('Failed to fetch job types: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error fetching job types:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error fetching job types.');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterJobTypes = () => {
    if (!searchTerm.trim()) {
      setFilteredJobTypes(jobTypes);
    } else {
      const filtered = jobTypes.filter(jt =>
        jt.JobDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
        jt.JobCode?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredJobTypes(filtered);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.JobDescription.trim()) {
      newErrors.JobDescription = 'Job description is required';
    }
    
    if (!formData.JobModeID) {
      newErrors.JobModeID = 'Job mode is required';
    }
    
    if (!formData.JobCode?.trim()) {
      newErrors.JobCode = 'Job code is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    
    try {
      // Filter out empty revenue items
      const validRevenues = revenueItems.filter(item => item.RevenueTypeID);
      
      const payload = {
        ...formData,
        StatusSea: formData.StatusSea ? 1 : 0,
        revenues: validRevenues.map(item => ({
          RevenueTypeID: item.RevenueTypeID,
          Amount: parseFloat(item.Amount) || 0,
          Cost: parseFloat(item.Cost) || 0
        }))
      };
      
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/JobType.php', payload);
      
      if (response.data.success) {
        alert(editMode ? 'Job type updated successfully!' : 'Job type created successfully!');
        setShowModal(false);
        resetForm();
        fetchJobTypes();
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error saving job type:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error saving job type: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = async (jobType) => {
    try {
      // Fetch full details including revenue items
      const response = await apiClient.get(`/JobType.php?id=${jobType.JobTypeID}`);
      
      if (response.data.success) {
        const data = response.data.data;
        
        setFormData({
          JobTypeID: data.JobTypeID,
          JobDescription: data.JobDescription,
          JobModeID: data.JobModeID,
          JobCode: data.JobCode,
          JobPrefix: data.JobPrefix || '',
          StatusSea: data.StatusSea === 1,
          Remarks: data.Remarks || ''
        });
        
        // Set revenue items
        if (data.revenues && data.revenues.length > 0) {
          const mappedRevenues = data.revenues.map((rev, idx) => ({
            id: Date.now() + idx,
            RevenueTypeID: rev.RevenueTypeID,
            Amount: parseFloat(rev.Amount) || 0,
            Cost: parseFloat(rev.Cost) || 0,
            RevenueType: rev.RevenueType,
            RevenueCode: rev.RevenueCode
          }));
          mappedRevenues.push({ id: Date.now() + data.revenues.length, RevenueTypeID: '', Amount: 0, Cost: 0, RevenueType: '', RevenueCode: '' });
          setRevenueItems(mappedRevenues);
        } else {
          setRevenueItems([{ id: Date.now(), RevenueTypeID: '', Amount: 0, Cost: 0, RevenueType: '', RevenueCode: '' }]);
        }
        
        setEditMode(true);
        setShowModal(true);
        setErrors({});
      }
    } catch (error) {
      console.error('Error loading job type:', error);
      alert('Error loading job type details');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this job type?')) {
      return;
    }
    
    try {
      const response = await apiClient.delete('/JobType.php', {
        data: { JobTypeID: id }
      });
      
      if (response.data.success) {
        alert('Job type deleted successfully!');
        fetchJobTypes();
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error deleting job type:', error);
      alert('Error deleting job type');
    }
  };

  const resetForm = () => {
    setFormData({
      JobTypeID: null,
      JobDescription: '',
      JobModeID: '',
      JobCode: '',
      JobPrefix: '',
      StatusSea: 1,
      Remarks: ''
    });
    setRevenueItems([{ id: Date.now(), RevenueTypeID: '', Amount: 0, Cost: 0, RevenueType: '', RevenueCode: '' }]);
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  // Revenue Grid Functions
  const handleRevenueTypeChange = (index, revenueTypeId) => {
    const selectedRevenue = revenueTypes.find(rt => rt.RevenueTypeID === parseInt(revenueTypeId));
    
    const newItems = [...revenueItems];
    newItems[index].RevenueTypeID = revenueTypeId;
    
    if (selectedRevenue) {
      newItems[index].Amount = parseFloat(selectedRevenue.MinimumValue) || 0;
      newItems[index].Cost = parseFloat(selectedRevenue.RevenueCost) || 0;
      newItems[index].RevenueType = selectedRevenue.RevenueType;
      newItems[index].RevenueCode = selectedRevenue.RevenueCode;
    }
    
    setRevenueItems(newItems);
    
    // Add new empty row if this is the last row and it has a value
    if (index === revenueItems.length - 1 && revenueTypeId) {
      addRevenueRow();
    }
  };

  const handleRevenueFieldChange = (index, field, value) => {
    const newItems = [...revenueItems];
    newItems[index][field] = value;
    setRevenueItems(newItems);
  };

  const addRevenueRow = () => {
    setRevenueItems([...revenueItems, { 
      id: Date.now(), 
      RevenueTypeID: '', 
      Amount: 0, 
      Cost: 0, 
      RevenueType: '', 
      RevenueCode: '' 
    }]);
  };

  const deleteRevenueRow = (index) => {
    if (revenueItems.length > 1) {
      const newItems = revenueItems.filter((_, i) => i !== index);
      setRevenueItems(newItems);
    }
  };

  // Check session context
  if (!companyId || !yearId) {
    return (
      <div className="master-container">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>Job Type Master</h2>
          <p className="subtitle">Manage job types and revenue structures</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} />
          Add Job Type
        </button>
      </div>

      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by description or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="toolbar-info">
          Total: {filteredJobTypes.length} {filteredJobTypes.length === 1 ? 'job type' : 'job types'}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading job types...</div>
      ) : filteredJobTypes.length === 0 ? (
        <div className="empty-state">
          <p>No job types found</p>
          {searchTerm && <button onClick={() => setSearchTerm('')}>Clear search</button>}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Job Code</th>
                <th>Description</th>
                <th>Job Mode</th>
                <th>Type</th>
                <th>Prefix</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredJobTypes.map((jobType) => (
                <tr key={jobType.JobTypeID}>
                  <td className="font-mono">{jobType.JobCode}</td>
                  <td className="font-semibold">{jobType.JobDescription}</td>
                  <td>{jobType.JobMode || 'N/A'}</td>
                  <td>
                    <span className={`type-badge ${jobType.StatusSea ? 'corporate' : 'individual'}`}>
                      {jobType.StatusSea ? 'Sea' : 'Air'}
                    </span>
                  </td>
                  <td>{jobType.JobPrefix || '-'}</td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(jobType)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(jobType.JobTypeID)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Job Type' : 'Add New Job Type'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              {/* Basic Info Section */}
              <div className="form-section">
                <h4>
                  <FileText size={18} />
                  Basic Information
                </h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="JobCode">
                      Job Code <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="JobCode"
                      value={formData.JobCode}
                      onChange={(e) => setFormData({ ...formData, JobCode: e.target.value.toUpperCase() })}
                      maxLength="20"
                      placeholder="e.g., IMP-SEA"
                      className={errors.JobCode ? 'error' : ''}
                    />
                    {errors.JobCode && (
                      <span className="error-message">{errors.JobCode}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="JobPrefix">
                      Job Prefix
                    </label>
                    <input
                      type="text"
                      id="JobPrefix"
                      value={formData.JobPrefix}
                      onChange={(e) => setFormData({ ...formData, JobPrefix: e.target.value })}
                      maxLength="10"
                      placeholder="e.g., JOB"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="JobDescription">
                    Job Description <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="JobDescription"
                    value={formData.JobDescription}
                    onChange={(e) => setFormData({ ...formData, JobDescription: e.target.value })}
                    maxLength="100"
                    placeholder="e.g., Import Sea Freight"
                    className={errors.JobDescription ? 'error' : ''}
                  />
                  {errors.JobDescription && (
                    <span className="error-message">{errors.JobDescription}</span>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="JobModeID">
                      Job Mode <span className="required">*</span>
                    </label>
                    <select
                      id="JobModeID"
                      value={formData.JobModeID}
                      onChange={(e) => setFormData({ ...formData, JobModeID: e.target.value })}
                      className={errors.JobModeID ? 'error' : ''}
                    >
                      <option value="">-- Select Job Mode --</option>
                      {jobModes.map((mode) => (
                        <option key={mode.JobModeID} value={mode.JobModeID}>
                          {mode.JobMode}
                        </option>
                      ))}
                    </select>
                    {errors.JobModeID && (
                      <span className="error-message">{errors.JobModeID}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Transport Type</label>
                    <div style={{ display: 'flex', gap: '20px', paddingTop: '10px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="StatusSea"
                          checked={formData.StatusSea === true || formData.StatusSea === 1}
                          onChange={() => setFormData({ ...formData, StatusSea: 1 })}
                          style={{ width: '20px', height: '20px', accentColor: '#667eea' }}
                        />
                        <span style={{ fontWeight: '600', color: '#374151' }}>Sea</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="StatusSea"
                          checked={formData.StatusSea === false || formData.StatusSea === 0}
                          onChange={() => setFormData({ ...formData, StatusSea: 0 })}
                          style={{ width: '20px', height: '20px', accentColor: '#667eea' }}
                        />
                        <span style={{ fontWeight: '600', color: '#374151' }}>Air</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="Remarks">Remarks</label>
                  <textarea
                    id="Remarks"
                    value={formData.Remarks}
                    onChange={(e) => setFormData({ ...formData, Remarks: e.target.value })}
                    rows="3"
                    placeholder="Additional notes..."
                  />
                </div>
              </div>

              {/* Revenue Items Grid */}
              <div className="form-section">
                <h4>Revenue Structure</h4>
                
                <div style={{ overflowX: 'auto', marginTop: '16px' }}>
                  <table className="data-table" style={{ minWidth: '600px' }}>
                    <thead>
                      <tr>
                        <th style={{ width: '40%' }}>Revenue Type</th>
                        <th style={{ width: '25%', textAlign: 'right' }}>Amount</th>
                        <th style={{ width: '25%', textAlign: 'right' }}>Cost</th>
                        <th style={{ width: '10%', textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenueItems.map((item, index) => (
                        <tr key={item.id}>
                          <td>
                            <select
                              value={item.RevenueTypeID}
                              onChange={(e) => handleRevenueTypeChange(index, e.target.value)}
                              style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            >
                              <option value="">-- Select Revenue Type --</option>
                              {revenueTypes.map((rt) => (
                                <option key={rt.RevenueTypeID} value={rt.RevenueTypeID}>
                                  {rt.RevenueType} ({rt.RevenueCode})
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <input
                              type="number"
                              step="0.01"
                              value={item.Amount}
                              onChange={(e) => handleRevenueFieldChange(index, 'Amount', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '6px',
                                fontSize: '14px',
                                textAlign: 'right'
                              }}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              step="0.01"
                              value={item.Cost}
                              onChange={(e) => handleRevenueFieldChange(index, 'Cost', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '6px',
                                fontSize: '14px',
                                textAlign: 'right'
                              }}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {revenueItems.length > 1 && (
                              <button
                                type="button"
                                onClick={() => deleteRevenueRow(index)}
                                className="btn-icon btn-delete"
                                title="Delete row"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <button
                  type="button"
                  onClick={addRevenueRow}
                  className="btn-secondary"
                  style={{ marginTop: '12px' }}
                >
                  <Plus size={16} />
                  Add Revenue Item
                </button>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  <Save size={18} />
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobType;